/**
 * CSV Parsing & Generation Utilities for User Bulk Import/Export
 */

// ── Types ──────────────────────────────────────────────────────────────────

export interface CsvImportRow {
  email: string;
  password: string;
  name: string;
  role: string;
  roles: string[];
  send_verification_email: boolean;
}

export interface CsvValidationResult {
  row: number;
  data: CsvImportRow;
  errors: string[];
  isValid: boolean;
}

export interface CsvExportUser {
  id: string;
  email: string;
  name: string;
  role: string;
  roles: string[];
  email_verified: boolean;
  is_active: boolean;
  locked: boolean;
  created_at: string;
  last_login_at: string;
}

// ── Constants ──────────────────────────────────────────────────────────────

export const IMPORT_COLUMNS = ['email', 'password', 'name', 'role', 'roles', 'send_verification_email'] as const;

export const EXPORT_COLUMNS = ['id', 'email', 'name', 'role', 'roles', 'email_verified', 'is_active', 'locked', 'created_at', 'last_login_at'] as const;

export const SAMPLE_CSV_TEMPLATE = `email,password,name,role,roles,send_verification_email
john@example.com,SecurePass1,John Doe,user,"user",true
jane@example.com,SecurePass2,Jane Admin,admin,"admin,editor",true
bob@example.com,StrongP4ss,Bob Smith,user,"user,viewer",false
`;

// ── CSV Parsing ────────────────────────────────────────────────────────────

/**
 * Parse a CSV string into an array of string arrays.
 * Handles quoted fields (including fields with commas and newlines inside quotes).
 */
export function parseCsvString(csv: string): string[][] {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = '';
  let inQuotes = false;
  let i = 0;

  while (i < csv.length) {
    const char = csv[i];
    const nextChar = csv[i + 1];

    if (inQuotes) {
      if (char === '"') {
        if (nextChar === '"') {
          // Escaped quote
          currentField += '"';
          i += 2;
        } else {
          // End of quoted field
          inQuotes = false;
          i++;
        }
      } else {
        currentField += char;
        i++;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
        i++;
      } else if (char === ',') {
        currentRow.push(currentField.trim());
        currentField = '';
        i++;
      } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
        currentRow.push(currentField.trim());
        if (currentRow.some(f => f !== '')) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = '';
        i += char === '\r' ? 2 : 1;
      } else {
        currentField += char;
        i++;
      }
    }
  }

  // Push last field/row
  currentRow.push(currentField.trim());
  if (currentRow.some(f => f !== '')) {
    rows.push(currentRow);
  }

  return rows;
}

/**
 * Parse roles string — supports:
 *   - Pipe-separated: "user|editor"
 *   - Comma-separated: "user,editor"  (inside a quoted CSV field)
 *   - JSON array: '["user","editor"]'
 *   - Single value: "user"
 */
function parseRolesField(value: string): string[] {
  const trimmed = value.trim();
  if (!trimmed) return [];

  // Try JSON array
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.map((r: string) => String(r).trim()).filter(Boolean);
    } catch {
      // Not valid JSON, fall through
    }
  }

  // Pipe-separated
  if (trimmed.includes('|')) {
    return trimmed.split('|').map(r => r.trim()).filter(Boolean);
  }

  // Comma-separated (most common inside quoted CSV fields)
  if (trimmed.includes(',')) {
    return trimmed.split(',').map(r => r.trim()).filter(Boolean);
  }

  // Single value
  return [trimmed];
}

// ── Validation ─────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_UPPER_RE = /[A-Z]/;
const PASSWORD_LOWER_RE = /[a-z]/;
const PASSWORD_DIGIT_RE = /[0-9]/;

function validateImportRow(data: CsvImportRow, rowNum: number): CsvValidationResult {
  const errors: string[] = [];

  // Email
  if (!data.email) {
    errors.push('Email is required');
  } else if (!EMAIL_RE.test(data.email)) {
    errors.push(`Invalid email format: "${data.email}"`);
  }

  // Password
  if (!data.password) {
    errors.push('Password is required');
  } else {
    if (data.password.length < 8) errors.push('Password must be at least 8 characters');
    if (!PASSWORD_UPPER_RE.test(data.password)) errors.push('Password needs at least one uppercase letter');
    if (!PASSWORD_LOWER_RE.test(data.password)) errors.push('Password needs at least one lowercase letter');
    if (!PASSWORD_DIGIT_RE.test(data.password)) errors.push('Password needs at least one digit');
  }

  // Role
  if (!data.role) {
    errors.push('Role is required');
  } else if (!['user', 'admin'].includes(data.role.toLowerCase())) {
    errors.push(`Role must be "user" or "admin", got "${data.role}"`);
  }

  // Name (optional but validate length)
  if (data.name && (data.name.length < 2 || data.name.length > 100)) {
    errors.push('Name must be between 2 and 100 characters');
  }

  return {
    row: rowNum,
    data,
    errors,
    isValid: errors.length === 0,
  };
}

/**
 * Parse and validate a full CSV file for user import.
 * Returns header-matched, validated rows.
 */
export function parseAndValidateCsv(csvContent: string): {
  results: CsvValidationResult[];
  headerErrors: string[];
} {
  const raw = parseCsvString(csvContent);
  const headerErrors: string[] = [];

  if (raw.length === 0) {
    headerErrors.push('CSV file is empty');
    return { results: [], headerErrors };
  }

  // Validate header row
  const header = raw[0].map(h => h.toLowerCase().trim());
  const requiredHeaders = ['email', 'password', 'role'];
  for (const req of requiredHeaders) {
    if (!header.includes(req)) {
      headerErrors.push(`Missing required column: "${req}"`);
    }
  }

  if (headerErrors.length > 0) {
    return { results: [], headerErrors };
  }

  // Build column index map
  const colIndex: Record<string, number> = {};
  header.forEach((col, idx) => { colIndex[col] = idx; });

  const results: CsvValidationResult[] = [];

  for (let i = 1; i < raw.length; i++) {
    const fields = raw[i];
    const get = (col: string) => (colIndex[col] !== undefined ? fields[colIndex[col]] || '' : '');

    const roleValue = get('role').toLowerCase().trim();
    const rolesRaw = get('roles');
    const parsedRoles = rolesRaw ? parseRolesField(rolesRaw) : [roleValue || 'user'];

    const sendVerifRaw = get('send_verification_email').toLowerCase().trim();
    const sendVerif = sendVerifRaw === '' || sendVerifRaw === 'true' || sendVerifRaw === '1' || sendVerifRaw === 'yes';

    const rowData: CsvImportRow = {
      email: get('email').trim(),
      password: get('password'),
      name: get('name').trim(),
      role: roleValue || 'user',
      roles: parsedRoles,
      send_verification_email: sendVerif,
    };

    results.push(validateImportRow(rowData, i));
  }

  // Check for duplicate emails within the file
  const emailCounts: Record<string, number[]> = {};
  results.forEach(r => {
    const e = r.data.email.toLowerCase();
    if (!emailCounts[e]) emailCounts[e] = [];
    emailCounts[e].push(r.row);
  });

  for (const [email, rows] of Object.entries(emailCounts)) {
    if (rows.length > 1) {
      for (const row of rows) {
        const result = results.find(r => r.row === row);
        if (result) {
          result.errors.push(`Duplicate email "${email}" found in rows: ${rows.join(', ')}`);
          result.isValid = false;
        }
      }
    }
  }

  return { results, headerErrors };
}

// ── CSV Generation (Export) ────────────────────────────────────────────────

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function generateExportCsv(users: CsvExportUser[]): string {
  const header = EXPORT_COLUMNS.join(',');
  const rows = users.map(u => {
    const rolesStr = (u.roles && u.roles.length > 0) ? u.roles.join(',') : u.role;
    return [
      escapeCsvField(u.id),
      escapeCsvField(u.email),
      escapeCsvField(u.name || ''),
      escapeCsvField(u.role),
      escapeCsvField(rolesStr),
      String(u.email_verified ?? false),
      String(u.is_active ?? true),
      String(u.locked ?? false),
      escapeCsvField(u.created_at || ''),
      escapeCsvField(u.last_login_at || ''),
    ].join(',');
  });

  return [header, ...rows].join('\n');
}

// ── File Helpers ───────────────────────────────────────────────────────────

export function downloadCsv(csvContent: string, filename: string) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}
